import { AppThunk } from 'models/store';
import { Dispatch } from 'react';
import { HttpHeaders } from 'models';
import {
  createDeleteHeaderData,
  createGetHeaderData,
  createHeaderData,
  createQueryParams
} from 'utils/restUtils';
import { apiClient } from 'services/restService';
import { AxiosError, AxiosResponse } from 'axios';
import { setUserList, setUserDetail } from '../eaiSlice';
import { openSnackbar } from 'features/app/appSlice';
import i18n from 'i18next';
import { UpdateUserRequestBody } from 'features/eai/eaiTypes';
import { logError } from 'services/Logging';

// ==============================|| USER THUNKS ||============================== //

export const getUserList =
  (
    email: string,
    size: number | undefined,
    page: number | undefined
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setUserList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );
    const queryParams = createQueryParams({
      email: email,
      size: size,
      page: page
    });

    apiClient
      .get(`/eai/users${queryParams}`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setUserList(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getUserDetail =
  (userId?: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setUserDetail>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );
    apiClient
      .get(`/eai/users/${userId}/detail`, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setUserDetail(response.data));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const enableUserAccount =
  (userId: string, callback?: () => void) =>
  (
    dispatch: Dispatch<ReturnType<typeof setUserDetail | typeof openSnackbar>>
  ) => {
    const headers: HttpHeaders = createHeaderData(true, 'application/hal+json');

    apiClient
      .patch(
        `/eai/users/${userId}/enable`,
        {},
        {
          headers
        }
      )
      .then((response: AxiosResponse) => {
        callback && callback();

        dispatch(setUserDetail(response.data));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.enableUserAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const disableUserAccount =
  (userId: string, callback?: () => void) =>
  (
    dispatch: Dispatch<ReturnType<typeof setUserDetail | typeof openSnackbar>>
  ) => {
    const headers: HttpHeaders = createHeaderData(true, 'application/hal+json');

    apiClient
      .patch(
        `/eai/users/${userId}/disable`,
        {},
        {
          headers
        }
      )
      .then((response: AxiosResponse) => {
        callback && callback();

        dispatch(setUserDetail(response.data));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.disableUserAccount'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const updateUser =
  (
    requestBody: Partial<UpdateUserRequestBody>,
    userId?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<ReturnType<typeof openSnackbar | typeof setUserDetail>>
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');

    apiClient
      .patch(`/eai/users/${userId}/detail`, { ...requestBody }, { headers })
      .then((response: AxiosResponse) => {
        dispatch(setUserDetail(response.data));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateUser'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback && callback();
      })
      .catch((error: AxiosError) => logError(error));
  };
export const getIsUserEmailAvailable =
  (
    email: string,
    userId?: string,
    callback?: (data: boolean) => void
  ): AppThunk =>
  () => {
    const headers: HttpHeaders = createGetHeaderData(true, '*/*');
    const queryParams = createQueryParams({
      email: email,
      userId: userId
    });

    apiClient
      .get(`/eai/users/email-available${queryParams}`, { headers })
      .then((response: AxiosResponse) => {
        callback && callback(response.data.available);
      })
      .catch((error: AxiosError) => logError(error));
  };

export const resetUserPassword =
  (userId: string, callback?: () => void) =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createHeaderData(true, 'application/hal+json');

    apiClient
      .patch(
        `/eai/users/${userId}/reset-password`,
        {},
        {
          headers
        }
      )
      .then((_response: AxiosResponse) => {
        callback && callback();

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.resetUserPassword'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
      })
      .catch((error: AxiosError) => logError(error));
  };

export const deleteUser =
  (userId: string, callback?: () => void) =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createDeleteHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .delete(`/eai/users/${userId}/detail`, {
        headers
      })
      .then((_response: AxiosResponse) => {
        callback && callback();

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.deleteUser'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );
      })
      .catch((error: AxiosError) => logError(error));
  };
